import { Avatar, Box, Card, CardContent, CardHeader, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Btn from '../../Components/Ui_elements/Btn';
import PopupModal from '../../Components/Ui_elements/PopupModal';
import AdvanceProjectSearch from './AdvanceProjectSearch';
import { useDispatch, useSelector } from 'react-redux';
import { advanceSearchProjectScoopData } from '../../redux/Thunk/ReportModule/ReportThunk';
import GenericTable from '../../Components/Ui_elements/GenericTable';
import CopyProjectNumber from '../Dashboard/ProjectOrderScoop/CopyProjectNumber';
import APP_CONFIG_URL_ENV from '../../env-config';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatDate, localizeNumber } from '../../Helper/generalhelper';
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Grid } from '@mui/material';
import { convertDotToComma } from '../../Helper/ConvertDotToComma';
import { setAdvanceSearchProjectScoopData, setAdvanceSearchProjectScoopFilters } from '../../redux/Slice/ReportModule/ReportSlice';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { showSnackbar } from '../../redux/Slice/snackbar/snackbarSlice';
import SearchField from '../../Components/Ui_elements/SearchField';


const AdvanceProjectIndex = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { advance_project_search, advance_project_search_filters } = useSelector(state => state.root.ReportModule);

    const [openModal, setOpenModal] = useState(false);
    const [payloadData, setPayloadData] = useState({});
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [order, setOrder] = useState({ column: "", dir: "asc" });
    const [searchText, setSearchText] = useState("");

    const fetchApi = (page, pageSize, order, searchText) => {
        const payload = {
            filters: payloadData.filters,
            perPage: pageSize,
            order: order.column ? [{ column: columns[order.column]?.sortPramas, dir: order.dir }] : [],
            search: searchText || "",
            page: page + 1,
        };
        dispatch(advanceSearchProjectScoopData(payload));
    };

    useEffect(() => {
        if (payloadData.filters && payloadData.filters.length > 0) {
            fetchApi(page, pageSize, order, searchText);
            // Save filters to Redux
            dispatch(setAdvanceSearchProjectScoopFilters(payloadData));
        }
    }, [payloadData, page, pageSize, order, searchText]);

    // On mount, check if we have saved filters in Redux and initialize payloadData
    useEffect(() => {
        if (advance_project_search_filters && advance_project_search_filters.filters && advance_project_search_filters.filters.length > 0) {
            setPayloadData(advance_project_search_filters);
        }
        // eslint-disable-next-line
    }, []);

    const handleExport = () => {
        if (!advance_project_search?.data || advance_project_search?.data.length === 0) {
            dispatch(showSnackbar({ message: "No data to export", type: "warning" }));
            return;
        }

        const exportData = advance_project_search.data.map(item => ({
            "Project Number": item?.orderNumber + "-" + String(item?.item_number || '').padStart(3, '0'),
            "Customer": item?.contactName,
            "Indirect Client": item?.accountname || "",
            "Source Language": item?.itemsSourceLang?.sourceLang || "",
            "Target Language": item?.itemsTargetLang?.sourceLang || "",
            "Deadline": item?.itemDuedate ? dayjs(item.itemDuedate).format("DD.MM.YYYY HH:mm") : "",
            "Project Manager": item?.pm_fullName,
            "Project Type": item?.project_type_name,
            "Prices": item?.totalAmount ? localizeNumber(item.totalAmount) + " " + item.price_currency : "",
            "PO Number": item?.itemPonumber,
            "Project Name": item?.scoopName,
            "Scoop Margin": convertDotToComma(item?.scoop_profit_margin) + " %",
            "Project Margin": convertDotToComma(item?.project_profit_margin) + " %",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
        saveAs(data, "Project_Search_Export.xlsx");
    };

    const data = advance_project_search?.data?.map((item, index) => ({
        rowNumber: page * pageSize + (index + 1),
        ...item,
    }));

    const columns = useMemo(() => {
        return [
            {
                name: "rowNumber",
                label: "#",
                options: { sort: false, }
            },
            {
                name: "orderNumber",
                sortPramas: "orderNumber",
                label: "Project Number",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        const rowData = advance_project_search?.data[tableMeta?.rowIndex];
                        const itemNumber = String(rowData?.item_number || '').padStart(3, '0');
                        const displayValue = `${value}-${itemNumber}`;
                        const scoopOnHold = rowData?.scoop_on_hold;

                        return (
                            <Typography
                                sx={{
                                    cursor: "pointer",
                                    color: "#1976d2",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                                onClick={() => navigate(`/general/scoop/${rowData?.orderId}`)} // Navigate to project details
                            >
                                {displayValue}
                            </Typography>
                        );
                    },
                    setCellProps: () => ({ style: { width: "150px" } }),
                },
            },
            {
                name: "contactName",
                sortPramas: "contactName",
                label: "Customer",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        const rowData = advance_project_search?.data[tableMeta?.rowIndex];
                        return (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {/* Customer Name with Logo */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        width: "100%",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            cursor: "pointer",
                                            "&:hover": {
                                                textDecoration: "underline",
                                            },
                                        }}
                                        onClick={() => navigate(`/viewclient/${rowData?.customer}`)}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    },
                    setCellProps: () => ({ style: { width: "200px" } }),
                },
            },
            {
                name: "accountname", // Assuming API returns accountname
                label: "Indirect Client",
                sortPramas: "accountname",
                options: {
                    setCellProps: () => ({ style: { width: "150px" } }),
                },
            },
            {
                name: "Language",
                label: "Language",
                options: {
                    customBodyRender: (_, tableMeta) => {
                        const rowData = advance_project_search?.data[tableMeta?.rowIndex];
                        const sourceLang = rowData?.itemsSourceLang;
                        const targetLang = rowData?.itemsTargetLang;

                        return (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {/* Source Language */}
                                {sourceLang?.sourceLang && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                        }}
                                    >
                                        {sourceLang.dataNgSrc && (
                                            <Avatar
                                                variant="square"
                                                sx={{ width: 25, height: 15 }}
                                                src={`${APP_CONFIG_URL_ENV.REACT_APP_FLAG_URL}${sourceLang.dataNgSrc}`}
                                                alt={sourceLang.alt || "Flag"}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flex: 1,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {sourceLang.sourceLang}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Target Language */}
                                {targetLang?.sourceLang && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            width: "100%",
                                        }}
                                    >
                                        {targetLang.dataNgSrc && (
                                            <Avatar
                                                variant="square"
                                                style={{ width: 25, height: 15 }}
                                                src={`${APP_CONFIG_URL_ENV.REACT_APP_FLAG_URL}${targetLang.dataNgSrc}`}
                                                alt={targetLang.alt || "Flag"}
                                            />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flex: 1,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {targetLang.sourceLang}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    },
                    setCellProps: () => ({ style: { width: "250px" } }),
                },
            },
            {
                name: "itemDuedate",
                label: "Deadline",
                sortPramas: "itemDuedate",
                options: {
                    setCellProps: () => ({ style: { width: "120px" } }),
                    customBodyRender: (value, tableMeta) => {
                        const rowData = advance_project_search?.data[tableMeta?.rowIndex];
                        if (value === "0000-00-00 00:00:00") {
                            return <Typography variant="body2"></Typography>;
                        }
                        const endDate = dayjs(value);
                        const cellDisplay = formatDate(value, { includeTime: true });

                        const startDateRaw = rowData?.itemStartdate;
                        const startDateFormatted = startDateRaw
                            ? formatDate(startDateRaw, { includeTime: true })
                            : "N/A";

                        const excludedStatusIds = [4, 5, 6, 7, 8, 9];
                        const statusId = rowData?.itemStatusId;

                        const now = dayjs();
                        const isToday =
                            endDate.date() === now.date() &&
                            endDate.month() === now.month() &&
                            endDate.year() === now.year();

                        let color = "inherit";
                        let fontWeight = "normal";

                        if (!excludedStatusIds.includes(statusId)) {
                            // Check if end date/time has passed
                            if (endDate.isBefore(now, "minute")) {
                                color = "error.main"; // red if overdue
                            } else if (isToday) {
                                fontWeight = "bold"; // due today
                            }
                        }

                        return (
                            <Tooltip
                                placement="top"
                                arrow
                                title={
                                    <Box
                                        sx={{
                                            bgcolor: "#f0f2f7",
                                            p: 1.5,
                                            borderRadius: 1,
                                            minWidth: 180,
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={2} color={"black"}>
                                            {/* Left Labels */}
                                            <Box>
                                                <Typography variant="caption" fontWeight={500}>
                                                    Start Date
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    End Date
                                                </Typography>
                                            </Box>

                                            {/* Vertical Line + Dots */}
                                            <Box display="flex" flexDirection="column" alignItems="center">
                                                <FiberManualRecordIcon sx={{ fontSize: 12, color: "#2196f3" }} />
                                                <Box
                                                    sx={{
                                                        width: "2px",
                                                        height: "20px",
                                                        bgcolor: "#2196f3",
                                                        my: "2px",
                                                    }}
                                                />
                                                <FiberManualRecordIcon sx={{ fontSize: 12, color: "#4caf50" }} />
                                            </Box>

                                            {/* Right Date Values */}
                                            <Box>
                                                <Typography variant="caption" display="block">
                                                    {startDateFormatted}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    {cellDisplay}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                }
                            >
                                <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                        color: color,
                                        fontWeight: fontWeight,
                                    }}
                                >
                                    {cellDisplay}
                                </Typography>
                            </Tooltip>
                        );
                    },
                },
            },
            {
                name: "pm_fullName",
                label: "Project Manager",
                sortPramas: "pm_fullName",
                options: { setCellProps: () => ({ style: { width: "150px" } }) },
            },
            {
                name: "project_type_name",
                label: "Project Type",
                sortPramas: "project_type_name",
                options: { setCellProps: () => ({ style: { width: "150px" } }) },
            },
            {
                name: "totalAmount",
                label: "Prices",
                sortPramas: "totalAmount",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        if (value === null || value === undefined || value === "") {
                            return <Typography variant="body2" noWrap></Typography>; // renders empty space
                        }

                        const price = localizeNumber(value);
                        const currency = advance_project_search?.data?.[tableMeta?.rowIndex]?.price_currency;

                        return (
                            <Typography variant="body2" noWrap>
                                {price} {currency}
                            </Typography>
                        );
                    },
                },
            },
            {
                name: "itemPonumber",
                label: "PO Number",
                sortPramas: "itemPonumber",
                options: { setCellProps: () => ({ style: { width: "120px" } }) },
            },
            {
                name: "scoopName",
                sortPramas: "scoopName",
                label: "Project Name",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        return (
                            <Box>
                                <Grid>
                                    <Typography variant="body2" noWrap>
                                        {value}
                                    </Typography>
                                </Grid>
                            </Box>
                        );
                    },
                    setCellProps: () => ({ style: { width: "250px" } }),
                },
            },
            {
                name: "scoop_profit_margin",
                label: "Scoop Margin",
                sortPramas: "scoop_profit_margin",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        return (
                            <Box>
                                <Grid>
                                    <Typography variant="body2" noWrap>
                                        {convertDotToComma(value)} %
                                    </Typography>
                                </Grid>
                            </Box>
                        );
                    },
                    setCellProps: () => ({ style: { width: "150px" } }),
                },
            },
            {
                name: "project_profit_margin",
                label: "Project Margin",
                sortPramas: "project_profit_margin",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        return (
                            <Box>
                                <Grid>
                                    <Typography variant="body2" noWrap>
                                        {convertDotToComma(value)} %
                                    </Typography>
                                </Grid>
                            </Box>
                        );
                    },
                    setCellProps: () => ({ style: { width: "150px" } }),
                },
            },
        ].filter(Boolean);
    }, [advance_project_search?.data,]);
    return (
        <>
            <Box>
                <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff" }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Advanced Project Search
                    </Typography>

                    {/* Filters always visible above the table */}
                    <AdvanceProjectSearch
                        setPayloadData={setPayloadData}
                        setOpenModal={() => { }} // no popup now
                    />
                </Box>

                <Box sx={{ p: 2, backgroundColor: "#fff" }} >
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} gap={2}>
                        <SearchField
                            onSearch={(value) => setSearchText(value)}
                            placeholder="Search"
                            size="small"
                            initialValue={searchText}
                        />
                        <Btn onClick={handleExport} variant="contained" size="small">
                            Export to Excel
                        </Btn>
                    </Box>
                    <GenericTable
                        columns={columns}
                        data={data}
                        showAction={false}
                        serverSide={true}
                        options={{
                            selectableRows: "none",
                            serverSide: true,
                            count: advance_project_search?.totalItems || 0,
                            page,
                            rowsPerPage: pageSize,
                            rowsPerPageOptions: [100, 200, 300],
                            search: false,
                            download: false,
                            print: false,
                            filter: false,
                            viewColumns: false,
                            sortOrder: {
                                name: columns[order?.column]?.name || "", // safe fallback
                                direction: order?.dir || "asc",
                            },
                            customToolbar: null,
                            onTableChange: (action, tableState) => {
                                switch (action) {
                                    case "changePage":
                                        setPage(tableState.page);
                                        break;
                                    case "changeRowsPerPage":
                                        setPageSize(tableState.rowsPerPage);
                                        setPage(0);
                                        break;
                                    case "sort":
                                        setOrder({
                                            column: tableState.activeColumn,
                                            dir: tableState.sortOrder.direction,
                                        });
                                        break;
                                    case "search":
                                        setSearchText(tableState.searchText || "");
                                        break;
                                    default:
                                        break;
                                }
                            },
                        }}
                    />
                </Box>
            </Box>

            <PopupModal
                maxWidth="md"
                fullWidth
                open={openModal}
                handleClose={() => { setOpenModal(false); }}
                title="Advance Project Search"
            >
                <AdvanceProjectSearch setPayloadData={setPayloadData} setOpenModal={setOpenModal} />
            </PopupModal>
        </>
    );
};

export default AdvanceProjectIndex